import { Component, computed, inject, type Signal } from "@angular/core";

import { AccessDeniedComponent } from "../../../shared/components/access-denied/access-denied.component";
import { AuthLayoutComponent } from "../../../shared/components/auth-layout/auth-layout.component";
import { AUTH_PROVIDER } from "../../../core/auth/auth-provider.token";
import { TENANT_CONTEXT } from "../../../core/auth/tenant-context.token";
import { AuthStateService } from "../../../core/services/auth-state.service";
import { BrandingService } from "../../../core/services/branding.service";
import { buildIncidentRef } from "../../../core/support/build-incident-ref";

import type { IPortalBranding } from "../../../core/models/i-portal-branding";
import type { IPortalUser } from "../../../core/models/i-portal-user";
import type { ITenantContext } from "../../../core/auth/i-tenant-context";

/**
 * "No access to this organization" — board 7f, left half.
 *
 * Reached from `davacoAdminGuard`, by a signed-in customer who opened a
 * Davaco-only route. A visitor who is not a member of the host's organization
 * — refused by Auth0, or holding another organization's token — goes to
 * /no-access instead, which renders the same board-7f card in its customer
 * variant. Both draw it through app-access-denied.
 *
 * Nothing is broken and nothing is retryable here, and signing in again with
 * the SAME account lands here again, so the one action is to sign out and use a
 * different account. Tenant-branded, because the visitor is looking at a
 * specific customer's address and the screen names it.
 */
@Component({
  selector: "app-forbidden",
  standalone: true,
  imports: [AuthLayoutComponent, AccessDeniedComponent],
  templateUrl: "./forbidden.component.html",
})
export class ForbiddenComponent {

  private readonly provider = inject(AUTH_PROVIDER);

  private readonly brandingService = inject(BrandingService);

  private readonly authState = inject(AuthStateService);

  private readonly tenant: ITenantContext = inject(TENANT_CONTEXT);

  public readonly branding: Signal<IPortalBranding> = this.brandingService.branding;

  /**
   * The address that was refused, or empty when the token carried none.
   *
   * Naming it is the most useful thing on the screen — the usual cause is a
   * visitor signed in with their personal or a previous employer's account, and
   * they cannot see which one the browser used.
   */
  public readonly email: Signal<string> = computed((): string => {
    const user: IPortalUser | null = this.authState.user();
    return user?.email ?? "";
  });

  /**
   * `org_membership_required · ref xxxx-xx`, the board's monospace line.
   *
   * The ref is derived from the refused subject and the host's slug rather than
   * generated, so it is the same on every reload and can be regenerated by
   * anyone holding those two facts. See `buildIncidentRef`.
   */
  public readonly reference: Signal<string> = computed((): string => {
    const user: IPortalUser | null = this.authState.user();
    const subject: string =
      user?.sub ?? this.authState.refusal()?.subject ?? "anonymous";
    return buildIncidentRef(subject, this.tenant.organization ?? "apex");
  });

  /**
   * Sign out, so the next attempt can use a different account.
   *
   * A plain navigation back to /login would NOT work: the session is still
   * valid, so the provider would silently re-issue the same token and the guard
   * would send the visitor straight back here. The sign-out is the action.
   *
   * After an Auth0 membership refusal it matters just as much: the refused
   * account still holds an Auth0 session, and only logging out of it lets the
   * next attempt choose a different account.
   */
  public async signOut(): Promise<void> {
    await this.provider.logout();
  }
}
